import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Search, Clock, Building2, Phone, User, FileText, Send, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
    new: 'bg-blue-100 text-blue-800',
    read: 'bg-gray-100 text-gray-600',
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

export const AdminContacts = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const LIMIT = 30;

    const load = useCallback(async () => {
        try {
            const res = await adminAPI.getContacts({ skip: page * LIMIT, limit: LIMIT });
            setContacts(res.data.contacts || []);
            setTotal(res.data.total || 0);
        } catch {
            toast.error('Erreur chargement contacts');
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => { load(); }, [load]);

    const filtered = search
        ? contacts.filter(c =>
            [c.name, c.company, c.email, c.subject].some(v => v?.toLowerCase().includes(search.toLowerCase()))
        )
        : contacts;

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-contacts-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-jungle flex items-center gap-2">
                        <Mail className="w-6 h-6" />
                        Messages de Contact
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">{total} message(s) au total</p>
                </div>
                <div className="relative max-w-xs w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                        data-testid="contacts-search"
                    />
                </div>
            </div>

            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jungle" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-16 text-muted-foreground">
                            <Mail className="h-12 w-12 mx-auto mb-3 opacity-40" />
                            <p>Aucun message de contact</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Nom</TableHead>
                                    <TableHead>Societe</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Sujet</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((c) => (
                                    <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" data-testid={`contact-row-${c.id}`}>
                                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <Clock className="w-3 h-3" />
                                                {formatDate(c.created_at)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">{c.name}</TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{c.company || '-'}</TableCell>
                                        <TableCell className="text-sm">{c.email}</TableCell>
                                        <TableCell className="text-sm max-w-[200px] truncate">{c.subject}</TableCell>
                                        <TableCell>
                                            <Badge className={c.status === 'replied' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}>
                                                {c.status === 'replied' ? 'Repondu' : 'Nouveau'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="outline" size="sm"
                                                onClick={() => setSelected(c)}
                                                data-testid={`view-contact-${c.id}`}>
                                                Voir
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {total > LIMIT && (
                <div className="flex items-center justify-center gap-2 mt-4">
                    <Button variant="outline" size="sm" disabled={page === 0}
                        onClick={() => setPage(p => p - 1)}>Precedent</Button>
                    <span className="text-sm text-muted-foreground">
                        Page {page + 1} / {Math.ceil(total / LIMIT)}
                    </span>
                    <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= total}
                        onClick={() => setPage(p => p + 1)}>Suivant</Button>
                </div>
            )}

            {/* Detail + Reply Dialog */}
            <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setReplyText(''); } }}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-jungle" />
                            Message de {selected?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {selected && (
                        <div className="space-y-4" data-testid="contact-detail-dialog">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Nom</p>
                                        <p className="font-medium">{selected.name}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-muted-foreground" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Societe</p>
                                        <p className="font-medium">{selected.company || '-'}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-muted-foreground" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Email</p>
                                        <p className="font-medium text-jungle">{selected.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                    <div>
                                        <p className="text-xs text-muted-foreground">Telephone</p>
                                        <p className="font-medium">{selected.phone || '-'}</p>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">Sujet</p>
                                <p className="font-semibold text-jungle">{selected.subject}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">Message</p>
                                <div className="bg-muted/50 rounded-lg p-4 text-sm whitespace-pre-wrap">{selected.message}</div>
                            </div>

                            {/* Previous replies */}
                            {selected.replies && selected.replies.length > 0 && (
                                <div className="space-y-2">
                                    <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                        Reponse(s) envoyee(s)
                                    </p>
                                    {selected.replies.map((r, i) => (
                                        <div key={`reply-${r.sent_at}-${i}`} className="bg-emerald-50 border border-emerald-100 rounded-lg p-3 text-sm">
                                            <p className="whitespace-pre-wrap text-gray-700">{r.message}</p>
                                            <p className="text-xs text-muted-foreground mt-2">{formatDate(r.sent_at)}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Reply form */}
                            <div className="border-t pt-4">
                                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                                    <Send className="w-4 h-4 text-jungle" />
                                    Repondre a {selected.name}
                                </p>
                                <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Ecrivez votre reponse ici..."
                                    rows={4}
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                                    data-testid="contact-reply-textarea"
                                />
                                <div className="flex items-center justify-between mt-3">
                                    <span className="text-xs text-muted-foreground">
                                        Recu le {formatDate(selected.created_at)}
                                    </span>
                                    <Button
                                        onClick={async () => {
                                            if (!replyText.trim()) {
                                                toast.error('Veuillez ecrire une reponse');
                                                return;
                                            }
                                            setSending(true);
                                            try {
                                                await adminAPI.replyContact(selected.id, { message: replyText.trim() });
                                                toast.success(`Reponse envoyee a ${selected.email}`);
                                                setReplyText('');
                                                setSelected(null);
                                                load();
                                            } catch {
                                                toast.error("Erreur lors de l'envoi de la reponse");
                                            } finally {
                                                setSending(false);
                                            }
                                        }}
                                        disabled={sending || !replyText.trim()}
                                        className="bg-jungle hover:bg-jungle/90"
                                        data-testid="contact-reply-send-btn"
                                    >
                                        {sending ? (
                                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4 mr-1" />
                                                Envoyer la reponse
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminContacts;
